import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Alert, Button, Center, Checkbox, Code, CopyButton, Group, Image, Paper, PinInput, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { api, mensajeDeError } from '../api.ts';
import { avisarOk } from '../formato.ts';
import { CLAVE_YO } from '../sesion.ts';

interface Alta { otpauth: string; qr: string; secreto: string }

/** Los códigos de respaldo, que se muestran una sola vez: copiar o descargar, y confirmar que se guardaron. */
export function MostrarCodigosRespaldo({ codigos, alConfirmar }: { codigos: string[]; alConfirmar: () => void }) {
  const [guardados, setGuardados] = useState(false);
  const texto = `Códigos de respaldo de epms (cada uno sirve una sola vez)\n\n${codigos.join('\n')}\n`;
  const descargar = () => {
    const url = URL.createObjectURL(new Blob([texto], { type: 'text/plain' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: 'epms-codigos-de-respaldo.txt' });
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Stack>
      <Alert color="orange" title="Guardá estos códigos de respaldo">
        Si perdés el celular, entrás con uno de estos códigos (cada uno sirve una sola vez). No se vuelven a mostrar.
        Guardalos en un lugar seguro, fuera del celular (un gestor de contraseñas, o impresos).
      </Alert>
      <SimpleGrid cols={2} spacing="xs">
        {codigos.map((c) => <Code key={c} fz="md" ta="center">{c}</Code>)}
      </SimpleGrid>
      <Group grow>
        <CopyButton value={texto}>{({ copied, copy }) => <Button variant="default" onClick={copy}>{copied ? 'Copiados' : 'Copiar'}</Button>}</CopyButton>
        <Button variant="default" onClick={descargar}>Descargar .txt</Button>
      </Group>
      <Checkbox label="Ya los guardé" checked={guardados} onChange={(e) => setGuardados(e.currentTarget.checked)} />
      <Button disabled={!guardados} onClick={alConfirmar}>Listo</Button>
    </Stack>
  );
}

/** Alta del segundo factor: QR, y confirmación con el primer código. */
export function AltaSegundoFactor({ alTerminar }: { alTerminar?: () => void }) {
  const qc = useQueryClient();
  const [alta, setAlta] = useState<Alta | null>(null);
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [codigos, setCodigos] = useState<string[] | null>(null);

  async function generar() {
    setError(null);
    setCargando(true);
    try {
      setAlta(await api.post<Alta>('/api/auth/2fa/configurar'));
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setCargando(false);
    }
  }

  async function activar(valor: string) {
    setError(null);
    setCargando(true);
    try {
      const r = await api.post<{ codigosRespaldo: string[] }>('/api/auth/2fa/activar', { codigo: valor });
      avisarOk('Segundo factor activado');
      // Primero los códigos de respaldo; el perfil se refresca recién al confirmar que se guardaron.
      setCodigos(r.codigosRespaldo);
    } catch (e) {
      setCodigo('');
      setError(mensajeDeError(e));
    } finally {
      setCargando(false);
    }
  }

  if (codigos) {
    return <MostrarCodigosRespaldo codigos={codigos} alConfirmar={() => { void qc.invalidateQueries({ queryKey: CLAVE_YO }); alTerminar?.(); }} />;
  }
  if (!alta) {
    return (
      <Stack>
        {error && <Alert color="red">{error}</Alert>}
        <Text size="sm">
          Vas a necesitar una app de autenticación en el celular: Google Authenticator, Microsoft Authenticator,
          1Password o similar.
        </Text>
        <Button onClick={() => void generar()} loading={cargando}>Generar código QR</Button>
      </Stack>
    );
  }
  return (
    <Stack align="center">
      {error && <Alert color="red" w="100%">{error}</Alert>}
      <Text size="sm">1. Escaneá este código con la app.</Text>
      <Image src={alta.qr} w={200} h={200} alt="Código QR para la app de autenticación" />
      <Text size="xs" c="dimmed" ta="center">Si no podés escanearlo, cargá esta clave a mano:<br /><Code>{alta.secreto}</Code></Text>
      <Text size="sm">2. Ingresá el código de 6 dígitos que muestra la app.</Text>
      <PinInput length={6} type="number" oneTimeCode value={codigo} onChange={setCodigo} onComplete={(v) => void activar(v)} disabled={cargando} />
    </Stack>
  );
}

export function Configurar2fa() {
  const navegar = useNavigate();
  return (
    <Center mih="100vh" p="md">
      <Paper withBorder shadow="sm" p="xl" w={420} maw="100%">
        <Stack>
          <Title order={3}>Segundo factor de ingreso</Title>
          <Text size="sm" c="dimmed">
            El estudio maneja claves y datos fiscales de clientes: para entrar hace falta, además de la contraseña,
            un código del celular.
          </Text>
          <AltaSegundoFactor alTerminar={() => navegar('/', { replace: true })} />
        </Stack>
      </Paper>
    </Center>
  );
}
